import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, Clock, Tag, ExternalLink, Loader2, Activity, TrendingUp,
  TrendingDown, Minus, AlertCircle, Sparkles, Building2, Layers,
} from "lucide-react";
import { Card, Badge, SectionLabel } from "../components/ui-bits";
import { liveAPI } from "../data/liveApi";

const SENTIMENT_META = {
  bullish: { tone: "positive", icon: TrendingUp, label: "BULLISH", color: "text-fij-green" },
  bearish: { tone: "negative", icon: TrendingDown, label: "BEARISH", color: "text-fij-red" },
  neutral: { tone: "neutral", icon: Minus, label: "NEUTRAL", color: "text-fij-textDim" },
};

function impactTone(score) {
  if (score >= 8) return { tone: "warning", color: "text-fij-red", label: "HIGH" };
  if (score >= 5) return { tone: "info", color: "text-fij-amber", label: "MEDIUM" };
  return { tone: "neutral", color: "text-fij-green", label: "LOW" };
}

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Kolkata",
    }) + " IST";
  } catch {
    return "—";
  }
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6" data-testid="event-details-loading">
      <div className="h-4 w-40 bg-fij-border/40 rounded-sm animate-pulse" />
      <Card className="animate-pulse space-y-3">
        <div className="flex gap-2">
          <div className="h-5 w-20 bg-fij-border/40 rounded-sm" />
          <div className="h-5 w-24 bg-fij-border/40 rounded-sm" />
        </div>
        <div className="h-8 w-3/4 bg-fij-border/40 rounded-sm" />
        <div className="h-4 w-full bg-fij-border/40 rounded-sm" />
        <div className="h-4 w-5/6 bg-fij-border/40 rounded-sm" />
      </Card>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i} className="animate-pulse space-y-2">
            <div className="h-3 w-24 bg-fij-border/40 rounded-sm" />
            <div className="h-6 w-32 bg-fij-border/40 rounded-sm" />
          </Card>
        ))}
      </div>
    </div>
  );
}

function StatCell({ label, value, valueClass = "", testId }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-fij-textMuted font-mono">{label}</div>
      <div
        data-testid={testId}
        className={`font-mono text-lg text-fij-text mt-1 ${valueClass}`}
      >
        {value}
      </div>
    </div>
  );
}

function TickerChip({ children, testId }) {
  return (
    <span
      data-testid={testId}
      className="inline-flex items-center font-mono text-[11px] text-fij-text bg-fij-surface border border-fij-border rounded-sm px-2 py-0.5"
    >
      {children}
    </span>
  );
}

function SectorChip({ children, testId }) {
  return (
    <span
      data-testid={testId}
      className="inline-flex items-center gap-1 font-mono text-[11px] text-fij-green bg-fij-green/5 border border-fij-green/30 rounded-sm px-2 py-0.5"
    >
      <Layers size={10} />
      {children}
    </span>
  );
}

export default function EventDetails() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    let retryTimer = null;

    const load = async (isRetry = false) => {
      if (!isRetry) setLoading(true);
      try {
        const d = await liveAPI.eventDetails(id);
        if (!alive) return;
        setData(d);
        setError(null);
        // If AI analysis is still being generated in the background, poll again in 6s.
        if (d?.analysis_pending) {
          retryTimer = setTimeout(() => load(true), 6000);
        }
      } catch (e) {
        if (!alive) return;
        setError(e?.response?.data?.detail || e.message || "Failed to load event.");
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => {
      alive = false;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [id]);

  if (loading && !data) return <LoadingSkeleton />;

  if (error && !data) {
    return (
      <div className="space-y-6">
        <Link
          to="/events"
          data-testid="event-back"
          className="inline-flex items-center gap-1.5 font-mono text-xs text-fij-textDim hover:text-fij-green transition-colors"
        >
          <ArrowLeft size={14} /> BACK TO EVENTS
        </Link>
        <Card className="border-fij-red/30 bg-fij-red/5" data-testid="event-error">
          <div className="flex items-start gap-3">
            <AlertCircle size={16} className="text-fij-red shrink-0 mt-0.5" />
            <div>
              <div className="font-mono text-sm text-fij-red">Event not available</div>
              <div className="text-fij-textDim text-xs mt-1">{error}</div>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  const ai = data?.analysis;
  const senti = SENTIMENT_META[ai?.sentiment] || SENTIMENT_META.neutral;
  const SentiIcon = senti.icon;
  const impact = impactTone(ai?.impact_score || 0);

  return (
    <div className="space-y-6" data-testid="event-details-page">
      <Link
        to="/events"
        data-testid="event-back"
        className="inline-flex items-center gap-1.5 font-mono text-xs text-fij-textDim hover:text-fij-green transition-colors"
      >
        <ArrowLeft size={14} /> BACK TO EVENTS
      </Link>

      {/* Hero */}
      <Card data-testid="event-hero">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {ai && <Badge tone={impact.tone}><Activity size={10} className="-mt-0.5" /> Impact {ai.impact_score}/10</Badge>}
          {ai && (
            <Badge tone={senti.tone}>
              <SentiIcon size={10} className="-mt-0.5" /> {senti.label}
            </Badge>
          )}
          {ai?.impact_duration && (
            <Badge tone="info"><Clock size={10} className="-mt-0.5" /> {ai.impact_duration}</Badge>
          )}
          {data?.category && <Badge tone="neutral"><Tag size={10} /> {data.category}</Badge>}
          {data?.published_at && (
            <Badge tone="neutral"><Clock size={10} /> {fmtTime(data.published_at)}</Badge>
          )}
        </div>

        <h1 className="text-xl sm:text-2xl text-fij-text leading-tight" data-testid="event-headline">
          {data?.headline || "—"}
        </h1>
        {data?.summary && (
          <p className="text-fij-textDim text-sm sm:text-base mt-3 leading-relaxed" data-testid="event-summary">
            {data.summary}
          </p>
        )}

        {data?.source_url && (
          <a
            href={data.source_url}
            target="_blank"
            rel="noreferrer"
            data-testid="event-source-link"
            className="mt-4 inline-flex items-center gap-1.5 font-mono text-xs text-fij-green hover:underline"
          >
            <ExternalLink size={12} /> {data.source || "Source article"} <span aria-hidden>→</span>
          </a>
        )}
      </Card>

      {/* AI in-progress state */}
      {data?.analysis_pending && (
        <Card className="flex items-start gap-3 border-fij-amber/40 bg-fij-amber/5" data-testid="event-ai-pending">
          <Loader2 size={16} className="text-fij-amber shrink-0 mt-0.5 animate-spin" />
          <div className="font-mono text-xs">
            <div className="text-fij-amber font-medium">Generating AI analysis…</div>
            <div className="text-fij-textDim mt-0.5">
              Claude Sonnet is analyzing this event. This page will refresh automatically in a few seconds.
            </div>
          </div>
        </Card>
      )}

      {ai && (
        <>
          {/* AI stats row */}
          <Card data-testid="event-ai-stats">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles size={14} className="text-fij-green" />
                <SectionLabel className="!mt-0">AI_ANALYSIS · CLAUDE_SONNET</SectionLabel>
              </div>
              {ai.updated_at && (
                <span className="font-mono text-[10px] text-fij-textMuted">
                  ANALYZED · {fmtTime(ai.updated_at)}
                </span>
              )}
            </div>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
              <StatCell
                label="Impact Score"
                testId="event-impact-score"
                value={`${ai.impact_score ?? "—"}/10`}
                valueClass={impact.color}
              />
              <StatCell
                label="Sentiment"
                testId="event-sentiment"
                value={senti.label}
                valueClass={senti.color}
              />
              <StatCell
                label="Confidence"
                testId="event-confidence"
                value={ai.confidence != null ? `${ai.confidence}%` : "—"}
              />
              <StatCell
                label="Impact Duration"
                testId="event-impact-duration"
                value={ai.impact_duration || "—"}
              />
            </div>
            {ai.impact_duration_reason && (
              <div className="mt-3 pt-3 border-t border-fij-border/60 text-xs text-fij-textDim font-mono" data-testid="event-impact-duration-reason">
                <span className="text-fij-textMuted uppercase">Duration read: </span>
                {ai.impact_duration_reason}
              </div>
            )}
            {ai.summary && (
              <div className="mt-3 pt-3 border-t border-fij-border/60">
                <div className="text-[10px] uppercase text-fij-textMuted font-mono mb-1">AI SUMMARY</div>
                <p className="text-sm text-fij-text leading-relaxed" data-testid="event-ai-summary">{ai.summary}</p>
              </div>
            )}
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            <div className="lg:col-span-2 space-y-6">
              {/* Affected sectors */}
              {ai.affected_sectors?.length > 0 && (
                <Card>
                  <SectionLabel>AFFECTED_SECTORS</SectionLabel>
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="event-sectors">
                    {ai.affected_sectors.map((s) => (
                      <SectorChip key={s} testId={`event-sector-${s.replace(/\s+/g, "-")}`}>{s}</SectorChip>
                    ))}
                  </div>
                </Card>
              )}

              {/* Affected stocks */}
              {ai.affected_stocks?.length > 0 && (
                <Card>
                  <SectionLabel>AFFECTED_STOCKS · NSE</SectionLabel>
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="event-stocks">
                    {ai.affected_stocks.map((s) => (
                      <TickerChip key={s} testId={`event-stock-${s}`}>
                        <Building2 size={10} className="mr-1 text-fij-textMuted" />{s}
                      </TickerChip>
                    ))}
                  </div>
                  {ai.affected_indices?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-fij-border/60">
                      <div className="text-[10px] uppercase text-fij-textMuted font-mono mb-2">Affected Indices</div>
                      <div className="flex flex-wrap gap-2" data-testid="event-indices">
                        {ai.affected_indices.map((s) => (
                          <TickerChip key={s} testId={`event-index-${s}`}>{s}</TickerChip>
                        ))}
                      </div>
                    </div>
                  )}
                </Card>
              )}

              {/* Similar events — AI-suggested */}
              {ai.similar_events?.length > 0 && (
                <Card>
                  <SectionLabel>SIMILAR_HISTORICAL_EVENTS</SectionLabel>
                  <ul className="mt-3 space-y-2" data-testid="event-similar-ai">
                    {ai.similar_events.map((e, i) => (
                      <li
                        key={i}
                        data-testid={`event-similar-ai-${i}`}
                        className="flex items-start gap-2 text-sm text-fij-text border-l border-fij-border pl-3 py-1"
                      >
                        <span className="text-fij-green font-mono">›</span>
                        <span>{e}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              )}

              {/* Similar stored — from our own analyses */}
              {data?.similar_stored?.length > 0 && (
                <Card>
                  <SectionLabel>SIMILAR_EVENTS_IN_JOURNAL</SectionLabel>
                  <ul className="mt-3 space-y-2" data-testid="event-similar-stored">
                    {data.similar_stored.map((s) => {
                      const si = SENTIMENT_META[s.sentiment] || SENTIMENT_META.neutral;
                      return (
                        <li key={s.key} data-testid={`event-similar-stored-${s.key.slice(0, 8)}`}>
                          <Link
                            to={`/events/${s.key}`}
                            className="flex items-start gap-3 p-2.5 border border-fij-border rounded-sm hover:border-fij-green/30 transition-colors group"
                          >
                            <div className="shrink-0 pt-0.5">
                              <si.icon size={14} className={si.color} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm text-fij-text group-hover:text-fij-green transition-colors line-clamp-2">
                                {s.headline}
                              </div>
                              <div className="font-mono text-[10px] text-fij-textMuted mt-1">
                                Impact {s.impact_score}/10 · {(s.affected_sectors || []).slice(0, 3).join(" · ")}
                              </div>
                            </div>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </Card>
              )}
            </div>

            <div className="space-y-6">
              {/* Watchlist */}
              {ai.watchlist?.length > 0 && (
                <Card>
                  <SectionLabel>WATCHLIST</SectionLabel>
                  <div className="mt-3 space-y-1.5" data-testid="event-watchlist">
                    {ai.watchlist.map((w) => (
                      <div
                        key={w}
                        data-testid={`event-watchlist-${w}`}
                        className="flex items-center justify-between font-mono text-sm text-fij-text border-b border-fij-border/40 py-1.5"
                      >
                        <span>{w}</span>
                        <span className="text-fij-textMuted text-[10px]">NSE</span>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Related news from same fetch */}
              {data?.related_news?.length > 0 && (
                <Card>
                  <SectionLabel>RELATED_COVERAGE · LIVE</SectionLabel>
                  <div className="mt-3 space-y-2" data-testid="event-related-news">
                    {data.related_news.map((n) => (
                      <Link
                        key={n.key || n.id}
                        to={`/events/${n.key || n.id}`}
                        data-testid={`event-related-${n.key ? n.key.slice(0, 8) : n.id}`}
                        className="block border border-fij-border rounded-sm p-2.5 hover:border-fij-green/30 transition-colors group"
                      >
                        <div className="text-xs text-fij-text group-hover:text-fij-green transition-colors line-clamp-2">
                          {n.headline}
                        </div>
                        <div className="font-mono text-[10px] text-fij-textMuted mt-1">
                          {n.source || "—"} · {fmtTime(n.published_at)}
                        </div>
                      </Link>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
